
import { ReferralId, ReferralIdBankStats, ReferralIdStatus } from './bank/types';

// Mock data for referral IDs
let referralIds: ReferralId[] = [];
let instanceAllocations: Record<string, number> = {};

// Generate a random referral code
const generateReferralCode = (): string => {
  const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let result = '';
  for (let i = 0; i < 8; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
};

// Initialize with some mock data
(() => {
  // Generate 100 referral IDs
  for (let i = 0; i < 100; i++) {
    referralIds.push({
      id: `ref-${i}`,
      code: generateReferralCode(),
      isAssigned: false,
      userId: '',
      instanceId: '',
      status: 'unused' as ReferralIdStatus,
      createdAt: new Date(Date.now() - Math.floor(Math.random() * 90) * 24 * 60 * 60 * 1000),
      updatedAt: new Date()
    });
  }
  
  // Allocate some to instances
  const instanceIds = ['inst-1', 'inst-2'];
  instanceIds.forEach(instanceId => {
    const count = Math.floor(Math.random() * 30) + 10; // 10-40 IDs per instance
    instanceAllocations[instanceId] = count;
    
    for (let i = 0; i < count; i++) {
      if (i < referralIds.length) {
        referralIds[i].instanceId = instanceId;
        
        // Mark some as assigned to users
        if (i % 3 === 0) {
          referralIds[i].isAssigned = true;
          referralIds[i].userId = `user-${i}`;
          referralIds[i].userName = `User ${i}`;
          referralIds[i].userEmail = `user${i}@example.com`;
          referralIds[i].assignedAt = new Date();
        }
      }
    }
  });
})();

// Get all referral IDs
export const getAllReferralIds = (): ReferralId[] => {
  return [...referralIds];
};

// Get bank statistics
export const getReferralIdBankStats = (): ReferralIdBankStats => {
  const totalIds = referralIds.length;
  const assignedIds = referralIds.filter(id => id.isAssigned).length;
  const allocatedToInstances = referralIds.filter(id => id.instanceId).length;
  const unassignedIds = totalIds - allocatedToInstances;
  
  return {
    totalIds,
    assignedIds,
    unassignedIds,
    instanceAllocations: { ...instanceAllocations },
    // Add the properties required by ReferralIdStats
    total: totalIds,
    used: assignedIds,
    available: unassignedIds,
    pending: 0
  };
};

// Check and replenish the bank
export const checkAndReplenishBank = (threshold: number = 100, generateCount: number = 1000): number => {
  const stats = getReferralIdBankStats();
  
  if (stats.unassignedIds < threshold) {
    // Generate new IDs
    const newIds: ReferralId[] = [];
    for (let i = 0; i < generateCount; i++) {
      newIds.push({
        id: `ref-${Date.now()}-${i}`,
        code: generateReferralCode(),
        isAssigned: false,
        userId: '',
        instanceId: '',
        status: 'unused' as ReferralIdStatus,
        createdAt: new Date(),
        updatedAt: new Date()
      });
    }
    
    referralIds = [...referralIds, ...newIds];
    return newIds.length;
  }
  
  return 0;
};

// Allocate referral IDs to an instance
export const allocateReferralIdsToInstance = (instanceId: string, planLimit: number): { 
  success: boolean; 
  allocated: number;
  remaining: number;
} => {
  const stats = getReferralIdBankStats();
  
  // Get current allocation for this instance
  const currentAllocated = instanceAllocations[instanceId] || 0;
  
  // Check if already at or above limit
  if (currentAllocated >= planLimit) {
    return { success: false, allocated: 0, remaining: currentAllocated };
  }
  
  // Calculate how many more to allocate
  const toAllocate = Math.min(planLimit - currentAllocated, stats.unassignedIds);
  
  if (toAllocate <= 0) {
    return { success: false, allocated: 0, remaining: currentAllocated };
  }
  
  // Get unallocated IDs
  const unallocatedIds = referralIds.filter(id => !id.instanceId);
  
  // Allocate IDs to this instance
  let allocated = 0;
  for (let i = 0; i < toAllocate; i++) {
    if (i < unallocatedIds.length) {
      const idIndex = referralIds.findIndex(id => id.id === unallocatedIds[i].id);
      if (idIndex !== -1) {
        referralIds[idIndex].instanceId = instanceId;
        allocated++;
      }
    }
  }
  
  // Update instance allocations count
  instanceAllocations[instanceId] = (instanceAllocations[instanceId] || 0) + allocated;
  
  return { 
    success: true, 
    allocated,
    remaining: instanceAllocations[instanceId]
  };
};

// Re-export the generateReferralIds function from bank/generator
export { generateReferralIds } from './bank/generator';
